import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  ArrowRight, 
  Home,
  Shield,
  Loader2,
  Lock,
  Sparkles,
  Check,
  CreditCard,
  Zap,
  ShieldCheck,
  Clock,
  Building2,
  X,
  Mail,
  Gift,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import useQuestionnaireStore from "@/store/useQuestionnaireStore";
import analytics from "@/utils/analytics";
import { hasClaimedDiscount, getDiscountCode } from "@/components/PartnerDiscountPopup";
import { getReferralCode } from "@/utils/referralTracking";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const outcomeConfig = {
  laag_risico: {
    title: "Laag risico",
    subtitle: "Goed nieuws!",
    description: "Op basis van uw antwoorden lijkt uw werksituatie weinig kenmerken te hebben van een dienstverband. U opereert waarschijnlijk als echte zelfstandige.",
    icon: CheckCircle2,
    colorClass: "text-emerald-600",
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-200",
    gradientClass: "from-emerald-50 to-emerald-100/50",
    advice: "Blijf alert op veranderingen in uw werksituatie en zorg dat uw overeenkomst actueel blijft."
  },
  twijfelgeval: {
    title: "Twijfelgeval",
    subtitle: "Let op",
    description: "Uw werksituatie bevat elementen die zowel wijzen op zelfstandigheid als op een mogelijke arbeidsrelatie. Extra aandacht is gewenst.",
    icon: AlertTriangle,
    colorClass: "text-amber-600",
    bgClass: "bg-amber-50",
    borderClass: "border-amber-200",
    gradientClass: "from-amber-50 to-amber-100/50",
    advice: "Wij adviseren om uw overeenkomst en werkpraktijk te laten beoordelen door een specialist."
  },
  verhoogd_risico: {
    title: "Verhoogd risico",
    subtitle: "Actie vereist",
    description: "Op basis van uw antwoorden zijn er meerdere signalen die kunnen wijzen op schijnzelfstandigheid. Actie wordt aanbevolen.",
    icon: XCircle,
    colorClass: "text-red-600",
    bgClass: "bg-red-50",
    borderClass: "border-red-200",
    gradientClass: "from-red-50 to-red-100/50",
    advice: "Wij raden u dringend aan om professioneel advies in te winnen over uw werksituatie."
  }
};

const Results = () => {
  const { submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { result, setResult } = useQuestionnaireStore();
  const [isLoading, setIsLoading] = useState(!result);
  const [error, setError] = useState(null);
  
  // Save results form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  
  // BV Waitlist modal
  const [showBVModal, setShowBVModal] = useState(false);
  const [bvEmail, setBvEmail] = useState("");
  const [isBVSubmitting, setIsBVSubmitting] = useState(false);
  const [bvSubmitted, setBvSubmitted] = useState(false);

  useEffect(() => {
    // Check for payment return params
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setShowPaymentSuccess(true);
      // Clear the URL params after showing
      window.history.replaceState({}, '', `/dba-check/resultaat/${submissionId}`);
    }
    
    // If we already have the result from submission, use it
    if (result && result.id === submissionId) {
      setIsSaved(result.is_saved);
      setIsPremiumUnlocked(result.premium_unlocked || false);
      if (paymentStatus === 'success') {
        // Refresh to get updated premium status
        fetchResultData();
      }
      return;
    }

    fetchResultData();
  }, [submissionId, searchParams]);

  const fetchResultData = async () => {
    try {
      const response = await axios.get(`${API_URL}/submissions/${submissionId}`);
      setResult(response.data);
      setIsSaved(response.data.is_saved);
      setIsPremiumUnlocked(response.data.premium_unlocked || false);
      if (response.data.name) setName(response.data.name);
      if (response.data.email) setEmail(response.data.email);
    } catch (err) {
      console.error("Error fetching result:", err);
      setError("Kon het resultaat niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveResults = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast.error("Vul alstublieft uw naam en e-mailadres in");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Vul een geldig e-mailadres in");
      return;
    }

    setIsSaving(true);
    
    // Track premium CTA click
    analytics.premiumCtaClicked(submissionId, 'save_results');

    try {
      const response = await axios.patch(`${API_URL}/submissions/${submissionId}`, {
        name: name.trim(),
        email: email.trim()
      });
      setResult(response.data);
      setIsSaved(true);
      toast.success("Uw resultaat is opgeslagen!");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== PAYMENT CONFIGURATION ====================
  // Real PayPro checkout URL
  const PAYPRO_PRODUCT_URL = "https://www.paypro.nl/product/Proofix_DBA_Analyse/127090";
  
  // Environment detection - RUNTIME hostname check (most reliable)
  // Only allow simulation on preview/localhost, NEVER on production domains
  const getPaymentEnvironment = () => {
    const hostname = window.location.hostname;
    const fullUrl = window.location.href;
    
    // PRODUCTION DOMAINS - Always use real PayPro
    const productionDomains = [
      'proofix.nl',
      'www.proofix.nl'
    ];
    
    // PREVIEW/DEV DOMAINS - Allow simulation
    const previewDomains = [
      'localhost',
      '127.0.0.1',
      'preview.emergentagent.com'
    ];
    
    const isProductionDomain = productionDomains.some(d => hostname === d || hostname.endsWith(`.${d}`));
    const isPreviewDomain = previewDomains.some(d => hostname === d || hostname.includes(d));
    
    // SAFETY: If not explicitly a preview domain, treat as production
    const isProduction = isProductionDomain || !isPreviewDomain;
    const isPreview = isPreviewDomain && !isProductionDomain;
    
    // DEBUG: Always log environment detection on page load
    console.log('╔══════════════════════════════════════════════════════════════╗');
    console.log('║           PAYMENT ENVIRONMENT DETECTION                       ║');
    console.log('╠══════════════════════════════════════════════════════════════╣');
    console.log(`║ hostname:          ${hostname}`);
    console.log(`║ fullUrl:           ${fullUrl}`);
    console.log(`║ isProductionDomain: ${isProductionDomain}`);
    console.log(`║ isPreviewDomain:    ${isPreviewDomain}`);
    console.log(`║ isProduction:       ${isProduction}`);
    console.log(`║ isPreview:          ${isPreview}`);
    console.log(`║ allowSimulation:    ${isPreview}`);
    console.log(`║ useRealPayPro:      ${isProduction}`);
    console.log('╠══════════════════════════════════════════════════════════════╣');
    if (isProduction) {
      console.log('║ 🔴 PRODUCTION MODE - Real PayPro redirect will be used       ║');
    } else {
      console.log('║ 🟡 PREVIEW MODE - Simulation enabled for testing             ║');
    }
    console.log('╚══════════════════════════════════════════════════════════════╝');
    
    return {
      hostname,
      fullUrl,
      isProduction,
      isPreview,
      allowSimulation: isPreview,
      useRealPayPro: isProduction
    };
  };
  
  const paymentEnv = getPaymentEnvironment();
  
  const handlePayment = async () => {
    // Prevent double clicks
    if (isProcessingPayment) return;
    
    setIsProcessingPayment(true);
    
    // Track payment CTA click
    analytics.premiumCtaClicked(submissionId, 'payment_initiated');
    
    // Detailed logging
    console.log('=== PAYMENT INITIATED ===');
    console.log(`[Payment] Hostname: ${paymentEnv.hostname}`);
    console.log(`[Payment] isProduction: ${paymentEnv.isProduction}`);
    console.log(`[Payment] isPreview: ${paymentEnv.isPreview}`);
    console.log(`[Payment] allowSimulation: ${paymentEnv.allowSimulation}`);
    console.log(`[Payment] useRealPayPro: ${paymentEnv.useRealPayPro}`);
    
    try {
      // Store submission info for webhook matching
      // The email will be used by PayPro webhook to match the payment
      const userEmail = result?.email || email;
      
      if (!userEmail) {
        toast.error("Vul eerst uw e-mailadres in om door te gaan");
        setIsProcessingPayment(false);
        return;
      }
      
      // Ensure submission has the email saved
      if (!result?.email && email) {
        await axios.patch(`${API_URL}/submissions/${submissionId}`, {
          name: name || result?.name || '',
          email: email.trim()
        });
      }
      
      // Store COMPLETE payment session data in localStorage for robust recovery
      const paymentSession = {
        submissionId: submissionId,
        email: userEmail.trim().toLowerCase(),
        initiatedAt: new Date().toISOString(),
        name: result?.name || name || ''
      };
      
      localStorage.setItem('proofix_payment_session', JSON.stringify(paymentSession));
      localStorage.setItem('pending_payment_submission', submissionId);
      
      console.log('[Payment] Stored payment session:', paymentSession);
      
      // ==================== PRODUCTION MODE ====================
      // ALWAYS redirect to real PayPro checkout
      if (paymentEnv.useRealPayPro) {
        console.log('[Payment] 🔴 PRODUCTION MODE - Redirecting to REAL PayPro checkout...');
        console.log(`[Payment] PayPro URL: ${PAYPRO_PRODUCT_URL}`);
        window.location.href = PAYPRO_PRODUCT_URL;
        return;
      }
      
      // ==================== PREVIEW/DEV MODE ====================
      // Only allow simulation on explicitly allowed domains
      if (paymentEnv.allowSimulation) {
        console.log('[Payment] 🟡 PREVIEW MODE - Using simulate-webhook endpoint...');
        
        try {
          // Use the dedicated simulation endpoint (blocked in production backend)
          const simulateResponse = await axios.post(
            `${API_URL}/payment/simulate-webhook?email=${encodeURIComponent(userEmail.trim().toLowerCase())}&submission_id=${submissionId}`
          );
          
          console.log('[Payment] Simulation response:', simulateResponse.data);
          
          if (simulateResponse.data.status === 'simulated_success') {
            // Redirect to payment success
            navigate(`/payment-success?submission_id=${submissionId}&email=${encodeURIComponent(userEmail)}`);
          } else {
            throw new Error('Unexpected simulation response');
          }
        } catch (simError) {
          console.error('[Payment] Simulation failed:', simError);
          
          // If simulation is blocked by backend, redirect to real PayPro
          if (simError.response?.status === 403) {
            console.log('[Payment] Simulation blocked by backend - falling back to PayPro redirect');
            window.location.href = PAYPRO_PRODUCT_URL;
          } else {
            toast.error("Test betaling mislukt. Probeer opnieuw.");
            setIsProcessingPayment(false);
          }
        }
        return;
      }
      
      // ==================== FALLBACK ====================
      // Unknown environment - ALWAYS redirect to real PayPro (safe default)
      console.log('[Payment] ⚠️ Unknown environment - Redirecting to REAL PayPro (safe default)...');
      window.location.href = PAYPRO_PRODUCT_URL;
      
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Er ging iets mis. Probeer het later opnieuw.");
      setIsProcessingPayment(false);
    }
  };

  // Handle BV waitlist modal
  const handleBVClick = async () => {
    // Track the click
    try {
      await axios.post(`${API_URL}/bv-waitlist/track-click?source=result_page`);
    } catch (e) {
      // Non-blocking
    }
    
    // Pre-fill email if known
    if (result?.email || email) {
      setBvEmail(result?.email || email);
    }
    
    setShowBVModal(true);
  };

  const handleBVSubmit = async (e) => {
    e.preventDefault();
    if (!bvEmail) return;
    
    setIsBVSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/bv-waitlist`, {
        email: bvEmail,
        source: "result_page",
        submission_id: submissionId
      });
      
      setBvSubmitted(true);
      
      if (response.data.already_registered) {
        toast.info("Je staat al op de wachtlijst!");
      }
    } catch (error) {
      console.error("BV waitlist error:", error);
      toast.error("Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setIsBVSubmitting(false);
    }
  };

  // Show payment success/loading screen
  if (showPaymentSuccess && isPremiumUnlocked) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-outfit text-2xl md:text-3xl font-medium text-white text-center mb-3"
        >
          Uw betaling is ontvangen!
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-zinc-400 text-center mb-8"
        >
          We maken uw persoonlijke rapport klaar...
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-6" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <Button
            onClick={() => navigate(`/dba-check/rapport/${submissionId}`)}
            className="rounded-full px-8 py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white"
          >
            Bekijk mijn rapport
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="loading-spinner" />
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-outfit text-2xl font-medium text-zinc-900 mb-2">
            {error || "Resultaat niet gevonden"}
          </h1>
          <p className="text-zinc-600 mb-6">
            Er ging iets mis bij het ophalen van uw resultaat.
          </p>
          <Link to="/dba-check">
            <Button className="rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white">
              Opnieuw proberen
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const outcome = outcomeConfig[result.outcome];
  const OutcomeIcon = outcome.icon;
  const scorePercentage = result.normalized_score;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="text-zinc-600 hover:text-zinc-900 transition-colors flex items-center gap-2 text-sm"
            data-testid="back-home-btn"
          >
            <Home className="w-4 h-4" />
            Terug naar Proofix
          </Link>
          <span className="font-outfit font-medium text-blue-600">DBA Check</span>
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          {/* Result Card - The main value */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`bg-gradient-to-br ${outcome.gradientClass} border-2 ${outcome.borderClass} shadow-lg rounded-2xl p-8 md:p-12 mb-8`}
            data-testid="result-card"
          >
            {/* Icon and Title */}
            <div className="text-center mb-8">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
                className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-white shadow-md mb-6`}
              >
                <OutcomeIcon className={`w-12 h-12 ${outcome.colorClass}`} />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`text-sm font-medium uppercase tracking-wider ${outcome.colorClass} mb-2`}
              >
                {outcome.subtitle}
              </motion.p>
              <motion.h1 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`font-outfit text-4xl sm:text-5xl font-medium text-zinc-900 mb-2`}
                data-testid="outcome-title"
              >
                {outcome.title}
              </motion.h1>
            </div>

            {/* Score visualization */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mb-8 bg-white/60 rounded-xl p-6"
            >
              <div className="flex justify-between text-sm text-zinc-600 mb-3">
                <span>Uw risicoscore</span>
                <span className="font-semibold text-zinc-900">{result.normalized_score}/100</span>
              </div>
              <div className="h-4 bg-white rounded-full overflow-hidden shadow-inner">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${scorePercentage}%` }}
                  transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                  className={`h-full rounded-full ${
                    result.outcome === "laag_risico" 
                      ? "bg-gradient-to-r from-emerald-400 to-emerald-500" 
                      : result.outcome === "twijfelgeval"
                      ? "bg-gradient-to-r from-amber-400 to-amber-500"
                      : "bg-gradient-to-r from-red-400 to-red-500"
                  }`}
                  data-testid="score-bar"
                />
              </div>
              <div className="flex justify-between text-xs text-zinc-400 mt-2">
                <span>0 - Laag</span>
                <span>33</span>
                <span>66</span>
                <span>100 - Hoog</span>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-white/80 rounded-xl p-6 mb-6"
            >
              <p className="text-zinc-700 leading-relaxed text-lg" data-testid="outcome-description">
                {outcome.description}
              </p>
            </motion.div>

            {/* Advice */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl p-6 border border-zinc-200/50"
            >
              <h3 className="font-outfit font-medium text-zinc-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Ons advies
              </h3>
              <p className="text-zinc-600 leading-relaxed" data-testid="outcome-advice">
                {outcome.advice}
              </p>
            </motion.div>
          </motion.div>

          {/* Urgency Banner - Only show if not saved */}
          {!isSaved && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className={`mb-4 px-6 py-4 rounded-xl border-2 ${
                result.outcome === "laag_risico" 
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : result.outcome === "twijfelgeval"
                  ? "bg-amber-50 border-amber-300 text-amber-800"
                  : "bg-red-50 border-red-300 text-red-800"
              }`}
              data-testid="urgency-banner"
            >
              <p className="flex items-center gap-2 font-medium text-sm">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                {result.outcome === "laag_risico" 
                  ? "Ook bij laag risico is het slim om je situatie goed te documenteren."
                  : result.outcome === "twijfelgeval"
                  ? "Zonder aanpassingen loop je mogelijk risico bij controle door de Belastingdienst."
                  : "Zonder aanpassingen loop je verhoogd risico bij controle door de Belastingdienst."}
              </p>
            </motion.div>
          )}

          {/* Premium Conversion Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className={`relative overflow-hidden rounded-2xl ${
              !isSaved 
                ? "bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 text-white shadow-2xl border border-zinc-700 blue-glow" 
                : "bg-white border border-zinc-200/60 shadow-sm"
            }`}
            data-testid="save-results-section"
          >
            {!isSaved ? (
              <>
                {/* Premium Header with Blue Gradient Accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1DA1F2] via-[#2563EB] to-[#1DA1F2]" />
                
                <div className="p-8 md:p-10">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-semibold uppercase tracking-wider mb-6">
                    <Sparkles className="w-3.5 h-3.5" />
                    Premium Analyse
                  </div>

                  {/* Title */}
                  <h3 className="font-outfit text-2xl md:text-3xl font-medium text-white mb-3">
                    Ontvang je volledige DBA analyse
                  </h3>
                  
                  {/* Subtext */}
                  <p className="text-zinc-300 text-lg leading-relaxed mb-8 max-w-xl">
                    Op basis van jouw antwoorden zien we {result.outcome === "laag_risico" ? "kansen om je positie te versterken" : "meerdere aandachtspunten"}. 
                    Ontvang een persoonlijke analyse met concrete verbeterpunten.
                  </p>

                  {/* Benefits Grid */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Persoonlijke risico-analyse</p>
                        <p className="text-sm text-zinc-400">Specifiek voor jouw situatie</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Concrete verbeteracties</p>
                        <p className="text-sm text-zinc-400">Stap voor stap uitgelegd</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Inzicht per onderdeel</p>
                        <p className="text-sm text-zinc-400">Alle 12 factoren beoordeeld</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Direct toepasbaar advies</p>
                        <p className="text-sm text-zinc-400">Vandaag nog aan de slag</p>
                      </div>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSaveResults} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-zinc-300 font-medium text-sm">
                          Jouw naam
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Jan Jansen"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="mt-2 rounded-xl bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 h-12"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-zinc-300 font-medium text-sm">
                          E-mailadres
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="jan@voorbeeld.nl"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="mt-2 rounded-xl bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 h-12"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSaving}
                      className="w-full rounded-xl py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white font-semibold text-lg shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                      data-testid="save-btn"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Even geduld...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Ontgrendel mijn rapport
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-zinc-500 text-center">
                      Gratis toegang • Geen creditcard nodig • Direct in je inbox
                    </p>
                  </form>
                </div>
              </>
            ) : (
              /* Saved - Show Premium Payment Section or Report Access */
              <div className="p-8 md:p-10">
                {/* Success message */}
                <div className="flex items-center gap-3 mb-6 text-emerald-600">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">Resultaat opgeslagen</p>
                    <p className="text-sm text-zinc-500">voor {result.email || email}</p>
                  </div>
                </div>

                {isPremiumUnlocked ? (
                  /* Premium Unlocked - Show Report Access */
                  <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 rounded-2xl p-8 border border-emerald-700/50">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h3 className="font-outfit text-2xl font-medium text-zinc-900 mb-2">
                        Premium Rapport Ontgrendeld!
                      </h3>
                      <p className="text-zinc-600 mb-6">
                        Uw persoonlijke DBA-rapport is klaar om te bekijken.
                      </p>
                      <Link to={`/dba-check/rapport/${submissionId}`}>
                        <Button
                          className="rounded-full px-8 py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white font-semibold text-lg"
                          data-testid="view-report-btn"
                        >
                          Bekijk mijn rapport
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  /* Premium Not Unlocked - Show Payment Section */
                  <>
                    {/* Social Proof */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex -space-x-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center">
                          <span className="text-xs text-blue-600">👤</span>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-blue-200 border-2 border-white flex items-center justify-center">
                          <span className="text-xs text-blue-600">👤</span>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-blue-300 border-2 border-white flex items-center justify-center">
                          <span className="text-xs text-blue-600">👤</span>
                        </div>
                      </div>
                      <p className="text-sm text-zinc-500">
                        <span className="font-medium text-zinc-700">Al door 1.000+ zzp'ers</span> gebruikt
                      </p>
                    </div>

                    {/* Premium Report Card */}
                    <div className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 rounded-2xl p-8 text-white relative overflow-hidden blue-glow">
                      {/* Blue accent line */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1DA1F2] via-[#2563EB] to-[#1DA1F2]" />
                      
                      <div className="flex items-center justify-between mb-6">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-semibold uppercase tracking-wider">
                          <Sparkles className="w-3.5 h-3.5" />
                          Premium Rapport
                        </div>
                        
                        {/* Intro price badge */}
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
                          <Clock className="w-3.5 h-3.5" />
                          Tijdelijke introductieprijs
                        </div>
                      </div>

                      <h3 className="font-outfit text-2xl font-medium text-white mb-3">
                        Persoonlijk DBA-rapport
                      </h3>
                      
                      <p className="text-zinc-300 leading-relaxed mb-6">
                        Ontvang een uitgebreid rapport met gedetailleerde analyse, 
                        concrete aanbevelingen en een actieplan voor jouw specifieke situatie.
                      </p>

                      {/* Benefits */}
                      <div className="grid sm:grid-cols-2 gap-3 mb-6">
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          Gedetailleerde analyse per vraag
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          Persoonlijke aanbevelingen
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          Concrete actiepunten
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          PDF download
                        </div>
                      </div>

                      {/* Urgency text */}
                      <div className="flex items-start gap-2 mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-200">
                          {result.outcome === "laag_risico" 
                            ? "Ook bij laag risico helpt een persoonlijk rapport je om je positie te versterken en compliant te blijven."
                            : "Op basis van jouw antwoorden zien we meerdere risico's die aandacht vereisen. Ons rapport geeft je concrete stappen."}
                        </p>
                      </div>

                      {/* Pricing Display */}
                      {(() => {
                        const hasDiscount = hasClaimedDiscount();
                        const discountCode = getDiscountCode();
                        const originalPrice = 19;
                        const discountedPrice = hasDiscount ? Math.round(originalPrice * 0.75) : originalPrice;
                        
                        return (
                          <div className="mb-6">
                            {hasDiscount && (
                              <div className="flex items-center justify-center gap-2 mb-3 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 w-fit mx-auto">
                                <Tag className="w-4 h-4 text-emerald-400" />
                                <span className="text-emerald-400 text-sm font-semibold">25% partnerkorting toegepast</span>
                              </div>
                            )}
                            <div className="flex items-center justify-center gap-3">
                              {hasDiscount ? (
                                <>
                                  <span className="text-zinc-500 line-through text-lg">€{originalPrice}</span>
                                  <span className="font-outfit text-4xl font-bold text-emerald-400">€{discountedPrice}</span>
                                  <span className="text-zinc-400 text-sm">eenmalig</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-zinc-500 line-through text-lg">€29</span>
                                  <span className="font-outfit text-4xl font-bold text-white">€{originalPrice}</span>
                                  <span className="text-zinc-400 text-sm">eenmalig</span>
                                </>
                              )}
                            </div>
                            {hasDiscount && discountCode && (
                              <div className="mt-3 text-center">
                                <p className="text-xs text-zinc-400">
                                  Kortingscode: <span className="font-mono text-blue-400 font-semibold">{discountCode}</span>
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">
                                  Voer deze code in bij afrekenen in PayPro
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* CTA Button */}
                      <div className="space-y-4">
                        <Button
                          onClick={handlePayment}
                          disabled={isProcessingPayment}
                          className="w-full rounded-xl py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white font-semibold text-lg shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                          data-testid="payment-btn"
                        >
                          {isProcessingPayment ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Even geduld...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              Ontvang mijn rapport – €{hasClaimedDiscount() ? '14' : '19'}
                            </>
                          )}
                        </Button>

                        {/* Trust indicators */}
                        <div className="flex items-center justify-center gap-6 text-xs text-zinc-400">
                          <span className="flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5" />
                            Direct geleverd
                          </span>
                          <span className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Geen abonnement
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5" />
                            100% privé
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.div>

          {/* BV Upsell Block - Only for twijfelgeval or verhoogd_risico */}
          {(result.outcome === "twijfelgeval" || result.outcome === "verhoogd_risico") && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-8 relative overflow-hidden"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/50 rounded-full blur-2xl" />
              
              {/* Label */}
              <div className="absolute top-4 right-4">
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  Volgende stap
                </span>
              </div>
              
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-outfit text-xl font-semibold text-zinc-900 mb-2">
                      Overweeg je een BV?
                    </h3>
                    <p className="text-zinc-600 mb-2">
                      Op basis van jouw antwoorden kan een BV in jouw situatie interessant zijn.
                    </p>
                    <p className="text-zinc-500 text-sm mb-4">
                      Vanaf een bepaalde winst kan een BV je honderden tot duizenden euro's per jaar besparen.
                    </p>
                    
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-zinc-700">
                        <Check className="w-4 h-4 text-blue-600" />
                        <span>Minder persoonlijk risico</span>
                      </li>
                      <li className="flex items-center gap-2 text-zinc-700">
                        <Check className="w-4 h-4 text-blue-600" />
                        <span>Mogelijk fiscaal voordeel</span>
                      </li>
                      <li className="flex items-center gap-2 text-zinc-700">
                        <Check className="w-4 h-4 text-blue-600" />
                        <span>Meer zekerheid in je structuur</span>
                      </li>
                    </ul>
                    
                    <Button
                      onClick={handleBVClick}
                      className="rounded-full px-6 py-5 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                      data-testid="bv-check-cta"
                    >
                      Check of jij kunt besparen met een BV
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* BV Waitlist Modal */}
          {showBVModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => !bvSubmitted && setShowBVModal(false)}
              />
              
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 overflow-hidden"
              >
                {/* Close button */}
                <button
                  onClick={() => setShowBVModal(false)}
                  className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                {/* Blue accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
                
                {bvSubmitted ? (
                  /* Success State */
                  <div className="text-center py-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-zinc-900 mb-2">
                      Tof — we laten het je weten zodra hij live is 👌
                    </h3>
                    <p className="text-zinc-500 mb-6">
                      Je ontvangt als eerste bericht wanneer de BV Check beschikbaar is.
                    </p>
                    <Button
                      onClick={() => setShowBVModal(false)}
                      className="rounded-full px-6 py-5 bg-zinc-900 hover:bg-zinc-800 text-white"
                    >
                      Sluiten
                    </Button>
                  </div>
                ) : (
                  /* Form State */
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-outfit text-lg font-semibold text-zinc-900">
                          Binnenkort beschikbaar: BV Check
                        </h3>
                      </div>
                    </div>
                    
                    <p className="text-zinc-600 mb-4">
                      We bouwen momenteel een tool die precies laat zien of een BV voor jou financieel interessant is.
                    </p>
                    
                    <p className="text-zinc-500 text-sm mb-6">
                      Laat je e-mailadres achter en krijg als eerste toegang.
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-6">
                      <p className="text-sm text-blue-700 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Als eerste toegang + mogelijk korting bij lancering
                      </p>
                    </div>
                    
                    <form onSubmit={handleBVSubmit}>
                      <div className="mb-4">
                        <Label htmlFor="bv-email" className="text-zinc-700 mb-2 block">
                          E-mailadres
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                          <Input
                            id="bv-email"
                            type="email"
                            value={bvEmail}
                            onChange={(e) => setBvEmail(e.target.value)}
                            placeholder="jan@voorbeeld.nl"
                            required
                            className="pl-10 rounded-xl h-12 border-zinc-300 focus:border-blue-500"
                            data-testid="bv-waitlist-email"
                          />
                        </div>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={isBVSubmitting}
                        className="w-full rounded-full py-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                        data-testid="bv-waitlist-submit"
                      >
                        {isBVSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Even geduld...
                          </>
                        ) : (
                          "Zet me op de wachtlijst"
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </motion.div>
            </div>
          )}

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 text-center"
          >
            <p className="text-xs text-zinc-400 leading-relaxed max-w-xl mx-auto" data-testid="disclaimer">
              Let op: Dit resultaat is indicatief en geeft geen juridisch of fiscaal advies. 
              De uitkomst is gebaseerd op uw eigen antwoorden en kan afwijken van de beoordeling 
              door de Belastingdienst.
            </p>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Link to="/" data-testid="home-link">
              <Button 
                variant="outline"
                className="rounded-full px-6 py-5 border-zinc-300 text-zinc-700 hover:bg-zinc-100 w-full sm:w-auto"
              >
                <Home className="w-4 h-4 mr-2" />
                Naar homepage
              </Button>
            </Link>
            <Link to="/dba-check" data-testid="restart-link">
              <Button 
                className="rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover w-full sm:w-auto"
              >
                Nieuwe check starten
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Results;
